/**
 * Full Topology Visualizer — Star Constellation View
 *
 * Displays all bank topology nodes on a single pannable/zoomable canvas
 * with force-directed layout clustering related nodes together.
 *
 * Sprint 36: Dual-View Bank Topology (Drill-Down + Full View)
 *
 * Features:
 * - All nodes visible at once (no drill-down)
 * - Force-directed layout for natural clustering
 * - Node sizes vary by level (larger = higher in hierarchy)
 * - Color coding by entity type
 * - Connection lines showing relationships
 * - Pan/zoom for navigation
 * - Light professional background
 * - Legend showing entity types
 */

import { TopologyNode, DEFAULT_LEVEL_COLORS } from '../topology-types';
import {
    INFRASTRUCTURE_DATA,
    APPLICATION_DATA,
    InfrastructureItem,
    ApplicationItem,
    groupInfrastructureByLocation,
    getInfrastructureColor,
    getApplicationColor,
} from '../topology-seed-infrastructure';
// =============================================================================
// Constants - Harvard Business Review Inspired Design
// =============================================================================

// Sophisticated neutral background with subtle warmth
const LIGHT_BG = '#fafbfc';
const CANVAS_PADDING = 80;
const MIN_WIDTH = 1200;
const MIN_HEIGHT = 850;

// Node sizes by level - clear visual hierarchy
const NODE_SIZES: Record<number, number> = {
    1: 45,  // Bank (root) - commanding presence
    2: 26,  // Business Unit - substantial
    3: 11,  // Capability - clear but subordinate
    4: 6,   // Process Flow - fine detail
    5: 4,   // Sub-process - subtle
};

// HBR-inspired color palette: sophisticated, muted, professional
// Using colors that work well in print and screen
const ENTITY_COLORS: Record<string, string> = {
    'bank': '#1a365d',          // Deep navy - authority
    'division': '#2c5282',      // Navy blue - structure
    'department': '#2b6cb0',    // Medium blue
    'business-unit': '#553c9a', // Deep purple - distinction
    'capability': '#276749',    // Forest green - growth/capability
    'process-flow': '#c05621',  // Burnt orange - action/flow
    'application': '#9c4221',   // Terracotta - technology
    'infrastructure': '#2c7a7b',// Teal - infrastructure
    'data': '#744210',          // Bronze - data/value
    'hr': '#285e61',            // Dark teal - people
    'default': '#4a5568',       // Sophisticated gray
};

// Orbital layout parameters - balanced spacing
const ORBIT_RADII: Record<number, number> = {
    2: 180,  // Business Units orbit around Bank
    3: 70,   // Capabilities orbit around their BU
    4: 35,   // Process Flows orbit around their Capability
    5: 20,   // Sub-processes orbit around their parent
};

// Infrastructure cluster positioning
const INFRA_CLUSTER_X_OFFSET = 380;  // Right of center
const APP_CLUSTER_X_OFFSET = -380;   // Left of center

// Sprint 39: AI Agent cluster positioning (top center)

// Infrastructure location colors
const LOCATION_COLORS: Record<string, string> = {
    'ap-southeast-2': '#0ea5e9',      // Sky blue (AWS Sydney)
    'ap-southeast-1': '#06b6d4',      // Cyan (AWS Singapore)
    'AWS': '#8b5cf6',                 // Purple (generic AWS)
    'Data Centre 1': '#64748b',       // Slate (DC1 - Production)
    'Data Centre 2': '#475569',       // Dark slate (DC2 - DR)
};

// Application domain colors
const DOMAIN_COLORS: Record<string, string> = {
    'Banking': '#1e40af',      // Deep blue
    'Cards': '#dc2626',        // Red
    'Lending': '#16a34a',      // Green
    'Customer': '#7c3aed',     // Purple
    'Digital': '#0ea5e9',      // Sky blue
    'Service': '#f59e0b',      // Amber
    'Sales': '#ec4899',        // Pink
    'Operations': '#64748b',   // Slate
    'Risk': '#ef4444',         // Red
    'Collections': '#f97316',  // Orange
    'Payments': '#06b6d4',     // Cyan
    'Analytics': '#8b5cf6',    // Purple
};

// Typography
const FONT_FAMILY = "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif";

// =============================================================================
// Types
// =============================================================================

interface NodePosition {
    id: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    node: TopologyNode;
    size: number;
    color: string;
    isRoot: boolean;  // Fixed at center
}

interface Edge {
    sourceId: string;
    targetId: string;
}

// =============================================================================
// FullTopologyVisualizer Class
// =============================================================================

export class FullTopologyVisualizer {
    private container: HTMLElement | null = null;
    private svg: SVGSVGElement | null = null;
    private width: number = MIN_WIDTH;
    private height: number = MIN_HEIGHT;
    private positions: Map<string, NodePosition> = new Map();
    private edges: Edge[] = [];

    // Pan/zoom state
    private zoom: number = 1;
    private panX: number = 0;
    private panY: number = 0;
    private isPanning: boolean = false;
    private lastMouseX: number = 0;
    private lastMouseY: number = 0;

    // DOM element references for hover highlighting
    private nodeElements: Map<string, SVGGElement> = new Map();
    private edgeElements: Map<string, SVGLineElement> = new Map();
    private childrenMap: Map<string, string[]> = new Map();  // parent ID -> child IDs

    // Infrastructure and Application node positions for cross-connections
    private infraPositions: Map<string, { x: number; y: number; item: InfrastructureItem }> = new Map();
    private appPositions: Map<string, { x: number; y: number; item: ApplicationItem }> = new Map();

    // Focus state - for click-to-center feature
    private focusedNodeId: string | null = null;
    private rootNode: TopologyNode | null = null;

    // Callbacks
    private nodeClickCallback: ((node: TopologyNode) => void) | null = null;
    private nodeDoubleClickCallback: ((node: TopologyNode) => void) | null = null;

    /**
     * Initialize the visualizer with a container element
     */
    public initialize(container: HTMLElement): void {
        this.container = container;
        this.width = Math.max(MIN_WIDTH, container.clientWidth || MIN_WIDTH);
        this.height = Math.max(MIN_HEIGHT, container.clientHeight || MIN_HEIGHT);
    }

    /**
     * Set callback for node click
     */
    public onNodeClick(callback: (node: TopologyNode) => void): void {
        this.nodeClickCallback = callback;
    }

    /**
     * Set callback for node double-click
     */
    public onNodeDoubleClick(callback: (node: TopologyNode) => void): void {
        this.nodeDoubleClickCallback = callback;
    }

    /**
     * Render the full topology tree on a single canvas
     */
    public render(rootNode: TopologyNode): void {
        if (!this.container) return;

        // Store root node for focus/reset
        this.rootNode = rootNode;
        this.focusedNodeId = null;

        // Clear existing
        this.container.innerHTML = '';
        this.positions.clear();
        this.edges = [];
        this.nodeElements.clear();
        this.edgeElements.clear();
        this.childrenMap.clear();
        this.infraPositions.clear();
        this.appPositions.clear();

        // Recalculate dimensions
        this.width = Math.max(MIN_WIDTH, this.container.clientWidth || MIN_WIDTH);
        this.height = Math.max(MIN_HEIGHT, this.container.clientHeight || MIN_HEIGHT);

        // Flatten tree to get all nodes and edges
        this.flattenTree(rootNode);

        // Run force-directed layout
        this.computeForceLayout();

        // Create SVG
        this.svg = this.createSvgElement();

        // Draw background
        this.drawBackground();

        // Draw orbital rings (faint circles showing orbits)
        this.drawOrbitalRings();

        // Draw edges (connections)
        this.drawEdges();

        // Draw nodes
        this.drawNodes();

        // Draw infrastructure cluster (right side)
        this.drawInfrastructureCluster();

        // Draw applications cluster (left side)
        this.drawApplicationsCluster();

        // Draw connections between infrastructure and applications
        this.drawInfraAppConnections();

        // Title is now in the HTML header bar (static, doesn't pan/zoom)

        // Setup pan/zoom
        this.setupPanZoom();

        this.container.appendChild(this.svg);

        // Draw legend as fixed overlay (outside SVG viewBox)
        this.drawLegendOverlay();
    }

    /**
     * Reset the view to default zoom and pan
     */
    public resetView(): void {
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.focusedNodeId = null;
        this.updateViewBox();
        this.clearFocusBlur();
    }

    /**
     * Focus on a specific node - centers view and blurs other nodes
     */
    public focusOnNode(nodeId: string): void {
        const pos = this.positions.get(nodeId);
        if (!pos) return;

        this.focusedNodeId = nodeId;

        // Get all descendant IDs for this node
        const focusedIds = this.getAllDescendantIds(nodeId);
        focusedIds.add(nodeId);

        // Also include ancestors up to root
        const ancestorIds = this.getAncestorIds(nodeId);
        ancestorIds.forEach(id => focusedIds.add(id));

        // Apply blur to non-focused elements
        this.applyFocusBlur(focusedIds);

        // Calculate zoom level based on node level (deeper = more zoom)
        const zoomLevels: Record<number, number> = {
            1: 1.2,   // Bank - slight zoom
            2: 1.8,   // Business Unit - moderate zoom
            3: 2.5,   // Capability - closer zoom
            4: 3.0,   // Process Flow - close zoom
            5: 3.5,   // Sub-process - very close
        };
        const targetZoom = zoomLevels[pos.node.level] || 2.0;

        // Animate to centered position
        this.animateToPosition(pos.x, pos.y, targetZoom);
    }

    /**
     * Get all descendant node IDs recursively
     */
    private getAllDescendantIds(nodeId: string): Set<string> {
        const descendants = new Set<string>();
        const childIds = this.childrenMap.get(nodeId) || [];

        for (const childId of childIds) {
            descendants.add(childId);
            const childDescendants = this.getAllDescendantIds(childId);
            childDescendants.forEach(id => descendants.add(id));
        }

        return descendants;
    }

    /**
     * Get ancestor node IDs up to root
     */
    private getAncestorIds(nodeId: string): Set<string> {
        const ancestors = new Set<string>();

        // Find parent by checking edges
        for (const edge of this.edges) {
            if (edge.targetId === nodeId) {
                ancestors.add(edge.sourceId);
                const parentAncestors = this.getAncestorIds(edge.sourceId);
                parentAncestors.forEach(id => ancestors.add(id));
                break;
            }
        }

        return ancestors;
    }

    /**
     * Apply blur effect to non-focused nodes
     */
    private applyFocusBlur(focusedIds: Set<string>): void {
        if (!this.svg) return;

        // Blur non-focused org nodes
        for (const [nodeId, element] of this.nodeElements) {
            if (focusedIds.has(nodeId)) {
                element.style.opacity = '1';
                element.style.filter = '';
            } else {
                element.style.opacity = '0.15';
                element.style.filter = 'blur(2px)';
            }
        }

        // Blur non-focused edges
        for (const [edgeKey, element] of this.edgeElements) {
            const [sourceId, targetId] = edgeKey.split('-');
            if (focusedIds.has(sourceId) && focusedIds.has(targetId)) {
                element.style.opacity = '1';
                element.style.filter = '';
            } else {
                element.style.opacity = '0.1';
                element.style.filter = 'blur(1px)';
            }
        }

        // Blur orbital rings
        const orbitsGroup = this.svg.querySelector('.orbits-group');
        if (orbitsGroup) {
            (orbitsGroup as SVGGElement).style.opacity = '0.15';
            (orbitsGroup as SVGGElement).style.filter = 'blur(1px)';
        }

        // Blur infrastructure and applications clusters
        const infraCluster = this.svg.querySelector('.infrastructure-cluster');
        const appsCluster = this.svg.querySelector('.applications-cluster');
        const infraAppConnections = this.svg.querySelector('.infra-app-connections');

        if (infraCluster) {
            (infraCluster as SVGGElement).style.opacity = '0.12';
            (infraCluster as SVGGElement).style.filter = 'blur(2px)';
        }
        if (appsCluster) {
            (appsCluster as SVGGElement).style.opacity = '0.12';
            (appsCluster as SVGGElement).style.filter = 'blur(2px)';
        }
        if (infraAppConnections) {
            (infraAppConnections as SVGGElement).style.opacity = '0.05';
            (infraAppConnections as SVGGElement).style.filter = 'blur(2px)';
        }
    }

    /**
     * Clear all focus blur effects
     */
    private clearFocusBlur(): void {
        if (!this.svg) return;

        // Reset org nodes
        for (const element of this.nodeElements.values()) {
            element.style.opacity = '1';
            element.style.filter = '';
        }

        // Reset edges
        for (const element of this.edgeElements.values()) {
            element.style.opacity = '1';
            element.style.filter = '';
        }

        // Reset orbital rings
        const orbitsGroup = this.svg.querySelector('.orbits-group');
        if (orbitsGroup) {
            (orbitsGroup as SVGGElement).style.opacity = '1';
            (orbitsGroup as SVGGElement).style.filter = '';
        }

        // Reset infrastructure and applications clusters
        const infraCluster = this.svg.querySelector('.infrastructure-cluster');
        const appsCluster = this.svg.querySelector('.applications-cluster');
        const infraAppConnections = this.svg.querySelector('.infra-app-connections');

        if (infraCluster) {
            (infraCluster as SVGGElement).style.opacity = '1';
            (infraCluster as SVGGElement).style.filter = '';
        }
        if (appsCluster) {
            (appsCluster as SVGGElement).style.opacity = '1';
            (appsCluster as SVGGElement).style.filter = '';
        }
        if (infraAppConnections) {
            (infraAppConnections as SVGGElement).style.opacity = '1';
            (infraAppConnections as SVGGElement).style.filter = '';
        }
    }

    /**
     * Animate pan/zoom to a target position
     */
    private animateToPosition(targetX: number, targetY: number, targetZoom: number): void {
        const duration = 400; // ms
        const startTime = performance.now();
        const startPanX = this.panX;
        const startPanY = this.panY;
        const startZoom = this.zoom;

        // Calculate target pan to center the node
        const targetPanX = targetX - this.width / (2 * targetZoom);
        const targetPanY = targetY - this.height / (2 * targetZoom);

        const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);

            this.zoom = startZoom + (targetZoom - startZoom) * eased;
            this.panX = startPanX + (targetPanX - startPanX) * eased;
            this.panY = startPanY + (targetPanY - startPanY) * eased;

            this.updateViewBox();

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }

    // =========================================================================
    // Orbital Layout - Moons around Planets
    // =========================================================================

    /**
     * Compute orbital positions for all nodes.
     * Bank at center, Business Units orbit the bank,
     * Capabilities orbit their BU, Process Flows orbit their Capability, etc.
     */
    private computeOrbitalLayout(node: TopologyNode, parentX: number, parentY: number, parentId?: string): void {
        const color = this.getColorForNode(node);
        const isRoot = node.level === 1;
        const size = isRoot ? 40 : (NODE_SIZES[node.level] || NODE_SIZES[5]);

        // Store this node's position
        this.positions.set(node.id, {
            id: node.id,
            x: parentX,
            y: parentY,
            vx: 0,
            vy: 0,
            node,
            size,
            color,
            isRoot,
        });

        // Create edge to parent
        if (parentId) {
            this.edges.push({ sourceId: parentId, targetId: node.id });
        }

        // Position children in orbit around this node
        const children = node.children;
        if (children.length === 0) return;

        // Build children map for hover highlighting
        this.childrenMap.set(node.id, children.map(c => c.id));

        const childLevel = node.level + 1;
        const orbitRadius = ORBIT_RADII[childLevel] || 20;

        // Distribute children evenly around the orbit
        const angleStep = (2 * Math.PI) / children.length;
        // Start from top (-90 degrees) for cleaner appearance
        const startAngle = -Math.PI / 2;

        children.forEach((child, index) => {
            const angle = startAngle + index * angleStep;
            const childX = parentX + orbitRadius * Math.cos(angle);
            const childY = parentY + orbitRadius * Math.sin(angle);

            // Recursively position this child and its descendants
            this.computeOrbitalLayout(child, childX, childY, node.id);
        });
    }

    /**
     * Build flat tree structure - just extracts nodes without positioning
     * (kept for compatibility, but we now use computeOrbitalLayout directly)
     */
    private flattenTree(node: TopologyNode): void {
        // Center point for the bank
        const centerX = this.width / 2;
        const centerY = this.height / 2 + 30;

        // Start orbital layout from root
        this.computeOrbitalLayout(node, centerX, centerY);
    }

    private getColorForNode(node: TopologyNode): string {
        const type = node.type?.toLowerCase() || '';

        // Check for specific type matches
        if (type.includes('division')) return ENTITY_COLORS['division'];
        if (type.includes('department')) return ENTITY_COLORS['department'];
        if (type.includes('business-unit') || type.includes('bu')) return ENTITY_COLORS['business-unit'];
        if (type.includes('capability')) return ENTITY_COLORS['capability'];
        if (type.includes('process') || type.includes('flow')) return ENTITY_COLORS['process-flow'];
        if (type.includes('application') || type.includes('app')) return ENTITY_COLORS['application'];
        if (type.includes('infrastructure') || type.includes('infra')) return ENTITY_COLORS['infrastructure'];
        if (type.includes('data')) return ENTITY_COLORS['data'];
        if (type.includes('hr') || type.includes('role')) return ENTITY_COLORS['hr'];

        // Fallback to level-based color
        return DEFAULT_LEVEL_COLORS[node.level] || ENTITY_COLORS['default'];
    }

    /**
     * No longer needed - orbital layout is computed during tree traversal
     */
    private computeForceLayout(): void {
        // No-op: orbital layout is computed in flattenTree -> computeOrbitalLayout
    }

    // =========================================================================
    // SVG Creation
    // =========================================================================

    private createSvgElement(): SVGSVGElement {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', String(this.height));
        svg.setAttribute('viewBox', `0 0 ${this.width} ${this.height}`);
        svg.style.fontFamily = 'ui-sans-serif, system-ui, -apple-system, sans-serif';
        svg.style.overflow = 'hidden';
        svg.style.background = LIGHT_BG;
        svg.style.borderRadius = '8px';
        svg.style.cursor = 'grab';
        return svg;
    }

    // =========================================================================
    // Drawing Methods
    // =========================================================================

    private drawBackground(): void {
        if (!this.svg) return;

        // Gradient background for depth
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        const bgGradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
        bgGradient.setAttribute('id', 'bgGradient');
        bgGradient.setAttribute('x1', '0%');
        bgGradient.setAttribute('y1', '0%');
        bgGradient.setAttribute('x2', '100%');
        bgGradient.setAttribute('y2', '100%');
        bgGradient.innerHTML = `
            <stop offset="0%" stop-color="#ffffff"/>
            <stop offset="50%" stop-color="#f8fafc"/>
            <stop offset="100%" stop-color="#f1f5f9"/>
        `;
        defs.appendChild(bgGradient);
        this.svg.appendChild(defs);

        const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bg.setAttribute('x', '0');
        bg.setAttribute('y', '0');
        bg.setAttribute('width', String(this.width));
        bg.setAttribute('height', String(this.height));
        bg.setAttribute('fill', 'url(#bgGradient)');
        this.svg.appendChild(bg);

        // Subtle grid pattern for professional look
        this.drawSubtleGrid();
    }

    /**
     * Draw subtle background grid for professional appearance
     */
    private drawSubtleGrid(): void {
        if (!this.svg) return;

        const gridGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        gridGroup.setAttribute('class', 'grid-group');
        gridGroup.setAttribute('opacity', '0.03');

        const gridSize = 40;
        for (let x = 0; x < this.width; x += gridSize) {
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', String(x));
            line.setAttribute('y1', '0');
            line.setAttribute('x2', String(x));
            line.setAttribute('y2', String(this.height));
            line.setAttribute('stroke', '#1a365d');
            line.setAttribute('stroke-width', '1');
            gridGroup.appendChild(line);
        }
        for (let y = 0; y < this.height; y += gridSize) {
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', '0');
            line.setAttribute('y1', String(y));
            line.setAttribute('x2', String(this.width));
            line.setAttribute('y2', String(y));
            line.setAttribute('stroke', '#1a365d');
            line.setAttribute('stroke-width', '1');
            gridGroup.appendChild(line);
        }

        this.svg.appendChild(gridGroup);
    }

    /**
     * Draw elegant orbital rings around each parent node
     */
    private drawOrbitalRings(): void {
        if (!this.svg) return;

        const orbitsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        orbitsGroup.setAttribute('class', 'orbits-group');

        for (const pos of this.positions.values()) {
            const childCount = pos.node.children.length;
            if (childCount === 0) continue;

            const childLevel = pos.node.level + 1;
            const orbitRadius = ORBIT_RADII[childLevel];
            if (!orbitRadius) continue;

            // Elegant orbital ring with subtle styling
            const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            ring.setAttribute('cx', String(pos.x));
            ring.setAttribute('cy', String(pos.y));
            ring.setAttribute('r', String(orbitRadius));
            ring.setAttribute('fill', 'none');
            ring.setAttribute('stroke', '#94a3b8');
            ring.setAttribute('stroke-width', '0.5');
            ring.setAttribute('stroke-dasharray', '2,6');
            ring.setAttribute('opacity', '0.4');
            orbitsGroup.appendChild(ring);
        }

        this.svg.appendChild(orbitsGroup);
    }

    private drawTitle(): void {
        if (!this.svg) return;

        // Title group with refined typography
        const titleGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

        // Main title
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        title.setAttribute('x', String(this.width / 2));
        title.setAttribute('y', '38');
        title.setAttribute('text-anchor', 'middle');
        title.setAttribute('fill', '#1a365d');
        title.setAttribute('font-size', '22');
        title.setAttribute('font-weight', '300');
        title.setAttribute('letter-spacing', '1');
        title.textContent = 'Enterprise Service Topology';
        titleGroup.appendChild(title);

        // Subtitle
        const subtitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        subtitle.setAttribute('x', String(this.width / 2));
        subtitle.setAttribute('y', '56');
        subtitle.setAttribute('text-anchor', 'middle');
        subtitle.setAttribute('fill', '#64748b');
        subtitle.setAttribute('font-size', '11');
        subtitle.setAttribute('font-weight', '400');
        subtitle.setAttribute('letter-spacing', '2');
        subtitle.textContent = 'ORGANIZATIONAL HIERARCHY & CAPABILITY MAP';
        titleGroup.appendChild(subtitle);

        // Decorative line
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(this.width / 2 - 120));
        line.setAttribute('y1', '68');
        line.setAttribute('x2', String(this.width / 2 + 120));
        line.setAttribute('y2', '68');
        line.setAttribute('stroke', '#cbd5e1');
        line.setAttribute('stroke-width', '1');
        titleGroup.appendChild(line);

        this.svg.appendChild(titleGroup);
    }

    private drawEdges(): void {
        if (!this.svg) return;

        const edgesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        edgesGroup.setAttribute('class', 'edges-group');

        for (const edge of this.edges) {
            const source = this.positions.get(edge.sourceId);
            const target = this.positions.get(edge.targetId);
            if (!source || !target) continue;

            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', String(source.x));
            line.setAttribute('y1', String(source.y));
            line.setAttribute('x2', String(target.x));
            line.setAttribute('y2', String(target.y));
            line.setAttribute('stroke', '#94a3b8');
            line.setAttribute('stroke-width', '1');
            line.setAttribute('stroke-opacity', '0.35');
            line.setAttribute('stroke-linecap', 'round');
            line.setAttribute('data-source', edge.sourceId);
            line.setAttribute('data-target', edge.targetId);
            edgesGroup.appendChild(line);

            // Store edge element for hover highlighting
            this.edgeElements.set(`${edge.sourceId}-${edge.targetId}`, line);
        }

        this.svg.appendChild(edgesGroup);
    }

    private drawNodes(): void {
        if (!this.svg) return;

        const nodesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        nodesGroup.setAttribute('class', 'nodes-group');

        // Sort by size (largest first) so smaller nodes render on top
        const sortedNodes = Array.from(this.positions.values())
            .sort((a, b) => b.size - a.size);

        for (const pos of sortedNodes) {
            const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            g.setAttribute('class', 'topology-node');
            g.style.cursor = 'pointer';

            let mainShape: SVGElement;

            if (pos.isRoot) {
                // Draw bank icon for root node
                mainShape = this.drawBankIcon(g, pos.x, pos.y, pos.size);
            } else {
                // Node circle for non-root nodes
                const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                circle.setAttribute('cx', String(pos.x));
                circle.setAttribute('cy', String(pos.y));
                circle.setAttribute('r', String(pos.size));
                circle.setAttribute('fill', pos.color);
                circle.setAttribute('stroke', '#ffffff');
                circle.setAttribute('stroke-width', pos.size > 15 ? '2' : '1');
                g.appendChild(circle);
                mainShape = circle;
            }

            // Labels for nodes (not root - has its own label)
            if (pos.node.level === 2) {
                // Business Unit: elegant label centered below with refined styling
                const labelText = this.truncateLabel(pos.node.label, 16);
                const labelWidth = labelText.length * 6 + 16;

                // Subtle shadow
                const labelShadow = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                labelShadow.setAttribute('x', String(pos.x - labelWidth / 2 + 1));
                labelShadow.setAttribute('y', String(pos.y + pos.size + 9));
                labelShadow.setAttribute('width', String(labelWidth));
                labelShadow.setAttribute('height', '20');
                labelShadow.setAttribute('rx', '3');
                labelShadow.setAttribute('fill', '#1a365d');
                labelShadow.setAttribute('opacity', '0.08');
                g.appendChild(labelShadow);

                // Label background - clean white with subtle border
                const labelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                labelBg.setAttribute('x', String(pos.x - labelWidth / 2));
                labelBg.setAttribute('y', String(pos.y + pos.size + 8));
                labelBg.setAttribute('width', String(labelWidth));
                labelBg.setAttribute('height', '20');
                labelBg.setAttribute('rx', '3');
                labelBg.setAttribute('fill', '#ffffff');
                labelBg.setAttribute('stroke', '#e2e8f0');
                labelBg.setAttribute('stroke-width', '1');
                g.appendChild(labelBg);

                // Colored accent bar on left
                const accent = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                accent.setAttribute('x', String(pos.x - labelWidth / 2));
                accent.setAttribute('y', String(pos.y + pos.size + 8));
                accent.setAttribute('width', '3');
                accent.setAttribute('height', '20');
                accent.setAttribute('rx', '1.5');
                accent.setAttribute('fill', pos.color);
                g.appendChild(accent);

                // Label text
                const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                label.setAttribute('x', String(pos.x + 2));
                label.setAttribute('y', String(pos.y + pos.size + 22));
                label.setAttribute('text-anchor', 'middle');
                label.setAttribute('fill', '#1e293b');
                label.setAttribute('font-size', '10');
                label.setAttribute('font-weight', '500');
                label.textContent = labelText;
                g.appendChild(label);
            } else if (pos.node.level === 3) {
                // Capability: refined smaller label
                const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                label.setAttribute('x', String(pos.x + pos.size + 5));
                label.setAttribute('y', String(pos.y + 3));
                label.setAttribute('fill', '#475569');
                label.setAttribute('font-size', '8');
                label.setAttribute('font-weight', '400');
                label.textContent = this.truncateLabel(pos.node.label, 14);
                g.appendChild(label);
            }

            // Store node element for hover highlighting
            this.nodeElements.set(pos.id, g);

            // Attach events
            this.attachNodeEvents(g, pos.node, mainShape as SVGCircleElement);

            nodesGroup.appendChild(g);
        }

        this.svg.appendChild(nodesGroup);
    }

    /**
     * Draw legend as fixed HTML overlay (stays in place during pan/zoom)
     * Positioned top-left, below the view toggle buttons
     */
    private drawLegendOverlay(): void {
        if (!this.container) return;

        // Remove existing legend if any
        const existingLegend = this.container.querySelector('.topology-legend-overlay');
        if (existingLegend) existingLegend.remove();

        // Ensure container has relative positioning for absolute child
        this.container.style.position = 'relative';

        // Create legend container
        const legend = document.createElement('div');
        legend.className = 'topology-legend-overlay';
        legend.style.cssText = `
            position: absolute;
            top: 12px;
            left: 12px;
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 14px 16px;
            font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 10px;
            box-shadow: 0 4px 12px rgba(26, 54, 93, 0.08);
            z-index: 100;
            min-width: 160px;
        `;

        // Build legend HTML
        const hierarchyItems = [
            { label: 'Bank (Institution)', color: ENTITY_COLORS['bank'], size: 14, shape: 'circle' },
            { label: 'Business Unit', color: ENTITY_COLORS['business-unit'], size: 11, shape: 'circle' },
            { label: 'Capability', color: ENTITY_COLORS['capability'], size: 8, shape: 'circle' },
            { label: 'Process Flow', color: ENTITY_COLORS['process-flow'], size: 5, shape: 'circle' },
        ];

        const infraItems = [
            { label: 'AWS Cloud', color: '#0ea5e9' },
            { label: 'Data Centre', color: '#64748b' },
            { label: 'Network', color: '#10b981' },
        ];

        const appItems = [
            { label: 'Core Banking', color: '#1e40af' },
            { label: 'Cards', color: '#dc2626' },
            { label: 'Lending', color: '#16a34a' },
            { label: 'Payments', color: '#06b6d4' },
        ];

        legend.innerHTML = `
            <div style="text-align: center; color: #1a365d; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; margin-bottom: 10px;">KEY</div>
            <div style="border-top: 1px solid #e2e8f0; margin-bottom: 10px;"></div>

            <div style="color: #64748b; font-size: 9px; font-weight: 500; letter-spacing: 1px; margin-bottom: 8px;">ORGANIZATION</div>
            ${hierarchyItems.map(item => `
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                    <span style="
                        display: inline-block;
                        width: ${item.size}px;
                        height: ${item.size}px;
                        background: ${item.color};
                        border-radius: 50%;
                        border: 1px solid #fff;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                        margin-right: 10px;
                        flex-shrink: 0;
                    "></span>
                    <span style="color: #334155; font-size: 9px;">${item.label}</span>
                </div>
            `).join('')}

            <div style="border-top: 1px solid #e2e8f0; margin: 8px 0;"></div>
            <div style="color: #2c7a7b; font-size: 9px; font-weight: 500; letter-spacing: 1px; margin-bottom: 8px;">INFRASTRUCTURE</div>
            ${infraItems.map(item => `
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                    <span style="
                        display: inline-block;
                        width: 12px;
                        height: 8px;
                        background: ${item.color};
                        border-radius: 2px;
                        border: 1px solid #fff;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                        margin-right: 10px;
                        flex-shrink: 0;
                    "></span>
                    <span style="color: #334155; font-size: 9px;">${item.label}</span>
                </div>
            `).join('')}

            <div style="border-top: 1px solid #e2e8f0; margin: 8px 0;"></div>
            <div style="color: #9c4221; font-size: 9px; font-weight: 500; letter-spacing: 1px; margin-bottom: 8px;">APPLICATIONS</div>
            ${appItems.map(item => `
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                    <span style="
                        display: inline-block;
                        width: 10px;
                        height: 10px;
                        background: ${item.color};
                        border-radius: 50%;
                        border: 1px solid #fff;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                        margin-right: 10px;
                        flex-shrink: 0;
                    "></span>
                    <span style="color: #334155; font-size: 9px;">${item.label}</span>
                </div>
            `).join('')}

            <div style="border-top: 1px solid #e2e8f0; margin: 8px 0;"></div>
            <div style="color: #64748b; font-size: 8px; font-weight: 500; letter-spacing: 0.5px; margin-bottom: 6px;">INTERACTIONS</div>
            <div style="color: #94a3b8; font-size: 8px; line-height: 1.4;">
                <div style="margin-bottom: 3px;">• Click node to focus</div>
                <div style="margin-bottom: 3px;">• Double-click to reset</div>
                <div>• Scroll to zoom</div>
            </div>
        `;

        this.container.appendChild(legend);
    }

    // =========================================================================
    // Pan/Zoom
    // =========================================================================

    private setupPanZoom(): void {
        if (!this.svg) return;

        // Mouse wheel zoom
        this.svg.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            this.handleZoom(delta, e.clientX, e.clientY);
        });

        // Click on background to reset focus
        this.svg.addEventListener('click', (e) => {
            const target = e.target as Element;
            // Only reset if clicking on background (rect) or SVG itself
            if (target.tagName === 'svg' || (target.tagName === 'rect' && !target.hasAttribute('data-infra-id'))) {
                if (this.focusedNodeId) {
                    this.resetView();
                }
            }
        });

        // Pan with drag
        this.svg.addEventListener('mousedown', (e) => {
            const target = e.target as Element;
            if (target.classList.contains('topology-node') || target.parentElement?.classList.contains('topology-node')) {
                return; // Don't pan when clicking nodes
            }
            this.isPanning = true;
            this.lastMouseX = e.clientX;
            this.lastMouseY = e.clientY;
            if (this.svg) this.svg.style.cursor = 'grabbing';
        });

        this.svg.addEventListener('mousemove', (e) => {
            if (!this.isPanning || !this.svg) return;

            const dx = e.clientX - this.lastMouseX;
            const dy = e.clientY - this.lastMouseY;
            this.lastMouseX = e.clientX;
            this.lastMouseY = e.clientY;

            const rect = this.svg.getBoundingClientRect();
            const scaleX = this.width / rect.width / this.zoom;
            const scaleY = this.height / rect.height / this.zoom;

            this.panX -= dx * scaleX;
            this.panY -= dy * scaleY;
            this.updateViewBox();
        });

        this.svg.addEventListener('mouseup', () => {
            this.isPanning = false;
            if (this.svg) this.svg.style.cursor = 'grab';
        });

        this.svg.addEventListener('mouseleave', () => {
            this.isPanning = false;
            if (this.svg) this.svg.style.cursor = 'grab';
        });
    }

    private handleZoom(factor: number, _clientX?: number, _clientY?: number): void {
        const newZoom = Math.max(0.3, Math.min(3, this.zoom * factor));
        if (newZoom === this.zoom) return;

        // Zoom toward center
        const centerX = this.panX + this.width / (2 * this.zoom);
        const centerY = this.panY + this.height / (2 * this.zoom);

        this.zoom = newZoom;

        this.panX = centerX - this.width / (2 * this.zoom);
        this.panY = centerY - this.height / (2 * this.zoom);

        this.updateViewBox();
    }

    private updateViewBox(): void {
        if (!this.svg) return;

        const w = this.width / this.zoom;
        const h = this.height / this.zoom;

        this.svg.setAttribute('viewBox', `${this.panX} ${this.panY} ${w} ${h}`);
    }

    // =========================================================================
    // Node Events
    // =========================================================================

    private attachNodeEvents(g: SVGGElement, node: TopologyNode, circle: SVGCircleElement): void {
        let clickTimeout: ReturnType<typeof setTimeout> | null = null;

        g.addEventListener('click', (e) => {
            e.stopPropagation();
            if (clickTimeout) clearTimeout(clickTimeout);
            clickTimeout = setTimeout(() => {
                // Focus on this node (center view and blur others)
                this.focusOnNode(node.id);

                // Also call external callback if set
                if (this.nodeClickCallback) {
                    this.nodeClickCallback(node);
                }
            }, 220);
        });

        g.addEventListener('dblclick', (e) => {
            e.stopPropagation();
            if (clickTimeout) {
                clearTimeout(clickTimeout);
                clickTimeout = null;
            }
            // Double-click resets view
            this.resetView();

            if (this.nodeDoubleClickCallback) {
                this.nodeDoubleClickCallback(node);
            }
        });

        // Hover effects - highlight node, children, and connecting edges
        const originalRadius = circle.getAttribute('r') || '10';
        g.addEventListener('mouseenter', () => {
            // Highlight the hovered node
            circle.setAttribute('r', String(parseFloat(originalRadius) * 1.3));
            circle.setAttribute('stroke-width', '3');
            circle.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';

            // Highlight children and their connecting edges
            this.highlightChildren(node.id, true);
        });
        g.addEventListener('mouseleave', () => {
            // Reset the hovered node
            circle.setAttribute('r', originalRadius);
            circle.setAttribute('stroke-width', parseFloat(originalRadius) > 15 ? '2' : '1');
            circle.style.filter = '';

            // Reset children and edges
            this.highlightChildren(node.id, false);
        });
    }

    /**
     * Highlight or reset children nodes and connecting edges
     */
    private highlightChildren(parentId: string, highlight: boolean): void {
        const childIds = this.childrenMap.get(parentId);
        if (!childIds) return;

        const parentPos = this.positions.get(parentId);
        if (!parentPos) return;

        for (const childId of childIds) {
            // Highlight the connecting edge
            const edgeKey = `${parentId}-${childId}`;
            const edge = this.edgeElements.get(edgeKey);
            if (edge) {
                if (highlight) {
                    edge.setAttribute('stroke', parentPos.color);
                    edge.setAttribute('stroke-width', '2.5');
                    edge.setAttribute('stroke-opacity', '0.85');
                } else {
                    edge.setAttribute('stroke', '#94a3b8');
                    edge.setAttribute('stroke-width', '1');
                    edge.setAttribute('stroke-opacity', '0.35');
                }
            }

            // Highlight the child node
            const childNode = this.nodeElements.get(childId);
            if (childNode) {
                const childCircle = childNode.querySelector('circle') as SVGCircleElement;
                if (childCircle) {
                    if (highlight) {
                        childCircle.style.filter = 'drop-shadow(0 0 6px rgba(0,0,0,0.4))';
                        childCircle.setAttribute('stroke-width', '2');
                    } else {
                        childCircle.style.filter = '';
                        const r = parseFloat(childCircle.getAttribute('r') || '10');
                        childCircle.setAttribute('stroke-width', r > 15 ? '2' : '1');
                    }
                }
            }
        }
    }

    // =========================================================================
    // Bank Icon Drawing
    // =========================================================================

    private drawBankIcon(g: SVGGElement, cx: number, cy: number, size: number): SVGElement {
        // Create a group for the bank icon
        const iconGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

        // Sophisticated gradient with HBR-style deep navy
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'radialGradient');
        gradient.setAttribute('id', 'bankGradient');
        gradient.innerHTML = `
            <stop offset="0%" stop-color="#2c5282"/>
            <stop offset="70%" stop-color="#1a365d"/>
            <stop offset="100%" stop-color="#0f172a"/>
        `;
        defs.appendChild(gradient);
        iconGroup.appendChild(defs);

        // Outer glow ring for emphasis
        const glowRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        glowRing.setAttribute('cx', String(cx));
        glowRing.setAttribute('cy', String(cy));
        glowRing.setAttribute('r', String(size + 4));
        glowRing.setAttribute('fill', 'none');
        glowRing.setAttribute('stroke', '#1a365d');
        glowRing.setAttribute('stroke-width', '1');
        glowRing.setAttribute('opacity', '0.2');
        iconGroup.appendChild(glowRing);

        // Outer circle - main node
        const outerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        outerCircle.setAttribute('cx', String(cx));
        outerCircle.setAttribute('cy', String(cy));
        outerCircle.setAttribute('r', String(size));
        outerCircle.setAttribute('fill', 'url(#bankGradient)');
        outerCircle.setAttribute('stroke', '#ffffff');
        outerCircle.setAttribute('stroke-width', '3');
        outerCircle.style.filter = 'drop-shadow(0 6px 12px rgba(26, 54, 93, 0.35))';
        iconGroup.appendChild(outerCircle);

        // Bank building icon (scaled to fit within the circle)
        const iconScale = size * 0.45;
        const iconY = cy - iconScale * 0.7;

        // Roof (classical pediment)
        const roof = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        const roofPoints = [
            `${cx},${iconY - iconScale * 0.5}`,
            `${cx - iconScale * 0.9},${iconY + iconScale * 0.05}`,
            `${cx + iconScale * 0.9},${iconY + iconScale * 0.05}`
        ].join(' ');
        roof.setAttribute('points', roofPoints);
        roof.setAttribute('fill', '#ffffff');
        iconGroup.appendChild(roof);

        // Entablature (horizontal band below roof)
        const entablature = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        entablature.setAttribute('x', String(cx - iconScale * 0.85));
        entablature.setAttribute('y', String(iconY + iconScale * 0.05));
        entablature.setAttribute('width', String(iconScale * 1.7));
        entablature.setAttribute('height', String(iconScale * 0.15));
        entablature.setAttribute('fill', '#e2e8f0');
        iconGroup.appendChild(entablature);

        // Building base
        const base = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        base.setAttribute('x', String(cx - iconScale * 0.8));
        base.setAttribute('y', String(iconY + iconScale * 0.2));
        base.setAttribute('width', String(iconScale * 1.6));
        base.setAttribute('height', String(iconScale * 0.95));
        base.setAttribute('fill', '#ffffff');
        iconGroup.appendChild(base);

        // Elegant pillars (4 columns)
        const pillarWidth = iconScale * 0.12;
        const pillarHeight = iconScale * 0.75;
        const pillarY = iconY + iconScale * 0.3;
        const pillarGap = iconScale * 0.38;

        for (let i = 0; i < 4; i++) {
            const pillar = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            const pillarX = cx - iconScale * 0.52 + i * pillarGap;
            pillar.setAttribute('x', String(pillarX));
            pillar.setAttribute('y', String(pillarY));
            pillar.setAttribute('width', String(pillarWidth));
            pillar.setAttribute('height', String(pillarHeight));
            pillar.setAttribute('rx', '1');
            pillar.setAttribute('fill', '#1a365d');
            iconGroup.appendChild(pillar);
        }

        // Steps (stylobate)
        const step = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        step.setAttribute('x', String(cx - iconScale * 0.9));
        step.setAttribute('y', String(iconY + iconScale * 1.1));
        step.setAttribute('width', String(iconScale * 1.8));
        step.setAttribute('height', String(iconScale * 0.12));
        step.setAttribute('fill', '#e2e8f0');
        iconGroup.appendChild(step);

        // Institution label below icon
        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', String(cx));
        label.setAttribute('y', String(cy + size + 22));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('fill', '#1a365d');
        label.setAttribute('font-size', '13');
        label.setAttribute('font-weight', '600');
        label.setAttribute('letter-spacing', '2');
        label.textContent = 'BANK';
        iconGroup.appendChild(label);

        g.appendChild(iconGroup);

        // Return the outer circle for hover effects
        return outerCircle;
    }

    // =========================================================================
    // Infrastructure & Applications Clusters
    // =========================================================================

    /**
     * Draw infrastructure cluster on the right side, grouped by location
     */
    private drawInfrastructureCluster(): void {
        if (!this.svg) return;

        const infraGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        infraGroup.setAttribute('class', 'infrastructure-cluster');

        // Position cluster on right side
        const centerX = this.width / 2 + INFRA_CLUSTER_X_OFFSET;
        const startY = 120;

        // Cluster title
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        title.setAttribute('x', String(centerX));
        title.setAttribute('y', String(startY - 30));
        title.setAttribute('text-anchor', 'middle');
        title.setAttribute('fill', '#2c7a7b');
        title.setAttribute('font-size', '12');
        title.setAttribute('font-weight', '600');
        title.setAttribute('letter-spacing', '1.5');
        title.textContent = 'INFRASTRUCTURE';
        infraGroup.appendChild(title);

        // Group by location
        const byLocation = groupInfrastructureByLocation();
        const locations = Array.from(byLocation.keys());

        let yOffset = startY;
        const locationSpacing = 160;
        const itemSpacing = 30;
        const nodeRadius = 8;

        for (const location of locations) {
            const items = byLocation.get(location) || [];
            const locationColor = LOCATION_COLORS[location] || '#64748b';

            // Location header
            const locationHeader = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            locationHeader.setAttribute('x', String(centerX));
            locationHeader.setAttribute('y', String(yOffset));
            locationHeader.setAttribute('text-anchor', 'middle');
            locationHeader.setAttribute('fill', locationColor);
            locationHeader.setAttribute('font-size', '10');
            locationHeader.setAttribute('font-weight', '500');
            locationHeader.textContent = location;
            infraGroup.appendChild(locationHeader);

            // Draw items in a circular/arc arrangement
            const itemCount = items.length;
            const arcRadius = Math.min(60, 20 + itemCount * 5);
            const arcStartAngle = -Math.PI / 2;
            const arcSpan = Math.PI;

            items.forEach((item, idx) => {
                const angle = arcStartAngle + (arcSpan * idx) / Math.max(1, itemCount - 1);
                const x = centerX + arcRadius * Math.cos(angle);
                const y = yOffset + 35 + arcRadius * Math.sin(angle) * 0.5 + 20;

                // Store position for connection drawing
                this.infraPositions.set(item.ciId, { x, y, item });

                // Node with infrastructure-specific color
                const infraColor = getInfrastructureColor(item.type);
                const node = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                node.setAttribute('x', String(x - nodeRadius));
                node.setAttribute('y', String(y - nodeRadius * 0.7));
                node.setAttribute('width', String(nodeRadius * 2));
                node.setAttribute('height', String(nodeRadius * 1.4));
                node.setAttribute('rx', '2');
                node.setAttribute('fill', infraColor);
                node.setAttribute('stroke', '#ffffff');
                node.setAttribute('stroke-width', '1');
                node.setAttribute('data-infra-id', item.ciId);
                node.style.cursor = 'pointer';

                // Tooltip on hover
                const tooltip = document.createElementNS('http://www.w3.org/2000/svg', 'title');
                tooltip.textContent = `${item.name}\n${item.type}\n${item.specification}\nSupports: ${item.supportsApplications.join(', ')}`;
                node.appendChild(tooltip);

                // Hover effect
                node.addEventListener('mouseenter', () => {
                    node.setAttribute('stroke-width', '2');
                    node.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
                    this.highlightInfraConnections(item.ciId, true);
                });
                node.addEventListener('mouseleave', () => {
                    node.setAttribute('stroke-width', '1');
                    node.style.filter = '';
                    this.highlightInfraConnections(item.ciId, false);
                });

                infraGroup.appendChild(node);

                // Small label for critical items
                if (item.criticality === 'Critical' && itemCount <= 8) {
                    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    label.setAttribute('x', String(x));
                    label.setAttribute('y', String(y + nodeRadius + 10));
                    label.setAttribute('text-anchor', 'middle');
                    label.setAttribute('fill', '#64748b');
                    label.setAttribute('font-size', '6');
                    label.textContent = this.truncateLabel(item.name, 12);
                    infraGroup.appendChild(label);
                }
            });

            yOffset += locationSpacing;
        }

        this.svg.appendChild(infraGroup);
    }

    /**
     * Draw applications cluster on the left side, grouped by domain
     */
    private drawApplicationsCluster(): void {
        if (!this.svg) return;

        const appsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        appsGroup.setAttribute('class', 'applications-cluster');

        // Position cluster on left side
        const centerX = this.width / 2 + APP_CLUSTER_X_OFFSET;
        const startY = 120;

        // Cluster title
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        title.setAttribute('x', String(centerX));
        title.setAttribute('y', String(startY - 30));
        title.setAttribute('text-anchor', 'middle');
        title.setAttribute('fill', '#9c4221');
        title.setAttribute('font-size', '12');
        title.setAttribute('font-weight', '600');
        title.setAttribute('letter-spacing', '1.5');
        title.textContent = 'APPLICATIONS';
        appsGroup.appendChild(title);

        // Group by domain
        const byDomain = this.groupApplicationsByDomain();
        const domains = Array.from(byDomain.keys());

        let yOffset = startY;
        const domainSpacing = 90;
        const nodeRadius = 7;

        for (const domain of domains) {
            const items = byDomain.get(domain) || [];
            const domainColor = DOMAIN_COLORS[domain] || '#64748b';

            // Domain header
            const domainHeader = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            domainHeader.setAttribute('x', String(centerX));
            domainHeader.setAttribute('y', String(yOffset));
            domainHeader.setAttribute('text-anchor', 'middle');
            domainHeader.setAttribute('fill', domainColor);
            domainHeader.setAttribute('font-size', '9');
            domainHeader.setAttribute('font-weight', '500');
            domainHeader.textContent = domain;
            appsGroup.appendChild(domainHeader);

            // Draw items horizontally
            const itemCount = items.length;
            const totalWidth = (itemCount - 1) * 22;
            const startX = centerX - totalWidth / 2;

            items.forEach((item, idx) => {
                const x = startX + idx * 22;
                const y = yOffset + 20;

                // Store position for connection drawing
                this.appPositions.set(item.name, { x, y, item });

                // Application node (circular)
                const appColor = getApplicationColor(item.domain);
                const node = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                node.setAttribute('cx', String(x));
                node.setAttribute('cy', String(y));
                node.setAttribute('r', String(item.criticality === 'Critical' ? nodeRadius + 2 : nodeRadius));
                node.setAttribute('fill', appColor);
                node.setAttribute('stroke', '#ffffff');
                node.setAttribute('stroke-width', '1');
                node.setAttribute('data-app-name', item.name);
                node.style.cursor = 'pointer';

                // Tooltip
                const tooltip = document.createElementNS('http://www.w3.org/2000/svg', 'title');
                tooltip.textContent = `${item.name}\n${item.vendor}\n${item.description}`;
                node.appendChild(tooltip);

                // Hover effect
                node.addEventListener('mouseenter', () => {
                    node.setAttribute('stroke-width', '2');
                    node.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
                    this.highlightAppConnections(item.name, true);
                });
                node.addEventListener('mouseleave', () => {
                    node.setAttribute('stroke-width', '1');
                    node.style.filter = '';
                    this.highlightAppConnections(item.name, false);
                });

                appsGroup.appendChild(node);
            });

            yOffset += domainSpacing;
        }

        this.svg.appendChild(appsGroup);
    }

    /**
     * Group applications by domain
     */
    private groupApplicationsByDomain(): Map<string, ApplicationItem[]> {
        const grouped = new Map<string, ApplicationItem[]>();

        for (const item of APPLICATION_DATA) {
            const domain = item.domain;
            if (!grouped.has(domain)) {
                grouped.set(domain, []);
            }
            grouped.get(domain)!.push(item);
        }

        return grouped;
    }

    /**
     * Draw connections between infrastructure and the applications they support
     */
    private drawInfraAppConnections(): void {
        if (!this.svg) return;

        const connectionsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        connectionsGroup.setAttribute('class', 'infra-app-connections');

        // Create a map of app name variations for matching
        const appNameMap = new Map<string, { x: number; y: number }>();
        for (const [name, pos] of this.appPositions) {
            // Store exact name
            appNameMap.set(name.toLowerCase(), { x: pos.x, y: pos.y });
            // Also store simplified versions for fuzzy matching
            const simplified = name.toLowerCase()
                .replace(/\s*\([^)]*\)\s*/g, '')  // Remove parentheses
                .replace(/platform|system/gi, '')
                .trim();
            appNameMap.set(simplified, { x: pos.x, y: pos.y });
        }

        // Draw connections from each infrastructure to its supported apps
        for (const [infraId, infraPos] of this.infraPositions) {
            const item = infraPos.item;

            for (const supportedApp of item.supportsApplications) {
                // Try to find matching application
                const appKey = supportedApp.toLowerCase();
                let appPos = appNameMap.get(appKey);

                // Try fuzzy matching if exact match fails
                if (!appPos) {
                    for (const [name, pos] of appNameMap) {
                        if (name.includes(appKey) || appKey.includes(name)) {
                            appPos = pos;
                            break;
                        }
                    }
                }

                if (appPos) {
                    // Draw curved connection line
                    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

                    // Calculate control point for curve (arc through center)
                    const midX = (infraPos.x + appPos.x) / 2;
                    const midY = (infraPos.y + appPos.y) / 2;
                    // Pull toward center of canvas
                    const centerX = this.width / 2;
                    const centerY = this.height / 2;
                    const pullFactor = 0.3;
                    const ctrlX = midX + (centerX - midX) * pullFactor;
                    const ctrlY = midY + (centerY - midY) * pullFactor;

                    const d = `M ${infraPos.x} ${infraPos.y} Q ${ctrlX} ${ctrlY} ${appPos.x} ${appPos.y}`;
                    path.setAttribute('d', d);
                    path.setAttribute('fill', 'none');
                    path.setAttribute('stroke', '#94a3b8');
                    path.setAttribute('stroke-width', '0.5');
                    path.setAttribute('stroke-opacity', '0.2');
                    path.setAttribute('stroke-dasharray', '3,3');
                    path.setAttribute('data-infra-id', infraId);
                    path.setAttribute('data-app-name', supportedApp);
                    path.classList.add('infra-app-link');

                    connectionsGroup.appendChild(path);
                }
            }
        }

        // Insert connections behind the clusters
        const nodesGroup = this.svg.querySelector('.nodes-group');
        if (nodesGroup) {
            this.svg.insertBefore(connectionsGroup, nodesGroup);
        } else {
            this.svg.appendChild(connectionsGroup);
        }
    }

    /**
     * Highlight connections when hovering on infrastructure node
     */
    private highlightInfraConnections(infraId: string, highlight: boolean): void {
        if (!this.svg) return;

        const connections = this.svg.querySelectorAll(`.infra-app-link[data-infra-id="${infraId}"]`);
        connections.forEach(conn => {
            if (highlight) {
                conn.setAttribute('stroke', '#2c7a7b');
                conn.setAttribute('stroke-width', '2');
                conn.setAttribute('stroke-opacity', '0.7');
                conn.setAttribute('stroke-dasharray', 'none');
            } else {
                conn.setAttribute('stroke', '#94a3b8');
                conn.setAttribute('stroke-width', '0.5');
                conn.setAttribute('stroke-opacity', '0.2');
                conn.setAttribute('stroke-dasharray', '3,3');
            }
        });

        // Also highlight connected application nodes
        const infraPos = this.infraPositions.get(infraId);
        if (infraPos) {
            for (const appName of infraPos.item.supportsApplications) {
                const appNode = this.svg.querySelector(`circle[data-app-name="${appName}"]`) as SVGCircleElement;
                if (appNode) {
                    if (highlight) {
                        appNode.setAttribute('stroke', '#2c7a7b');
                        appNode.setAttribute('stroke-width', '3');
                        appNode.style.filter = 'drop-shadow(0 0 6px rgba(44, 122, 123, 0.5))';
                    } else {
                        appNode.setAttribute('stroke', '#ffffff');
                        appNode.setAttribute('stroke-width', '1');
                        appNode.style.filter = '';
                    }
                }
            }
        }
    }

    /**
     * Highlight connections when hovering on application node
     */
    private highlightAppConnections(appName: string, highlight: boolean): void {
        if (!this.svg) return;

        // Find all infrastructure that supports this app
        const connections = this.svg.querySelectorAll('.infra-app-link');
        connections.forEach(conn => {
            const connAppName = conn.getAttribute('data-app-name') || '';
            const connInfraId = conn.getAttribute('data-infra-id') || '';

            // Check if this connection involves the hovered app
            const infraPos = this.infraPositions.get(connInfraId);
            if (infraPos && infraPos.item.supportsApplications.some(a =>
                a.toLowerCase().includes(appName.toLowerCase()) ||
                appName.toLowerCase().includes(a.toLowerCase())
            )) {
                if (highlight) {
                    conn.setAttribute('stroke', '#9c4221');
                    conn.setAttribute('stroke-width', '2');
                    conn.setAttribute('stroke-opacity', '0.7');
                    conn.setAttribute('stroke-dasharray', 'none');

                    // Highlight the connected infrastructure node
                    const infraNode = this.svg?.querySelector(`rect[data-infra-id="${connInfraId}"]`) as SVGRectElement;
                    if (infraNode) {
                        infraNode.setAttribute('stroke', '#9c4221');
                        infraNode.setAttribute('stroke-width', '3');
                        infraNode.style.filter = 'drop-shadow(0 0 6px rgba(156, 66, 33, 0.5))';
                    }
                } else {
                    conn.setAttribute('stroke', '#94a3b8');
                    conn.setAttribute('stroke-width', '0.5');
                    conn.setAttribute('stroke-opacity', '0.2');
                    conn.setAttribute('stroke-dasharray', '3,3');

                    // Reset infrastructure node
                    const infraNode = this.svg?.querySelector(`rect[data-infra-id="${connInfraId}"]`) as SVGRectElement;
                    if (infraNode) {
                        infraNode.setAttribute('stroke', '#ffffff');
                        infraNode.setAttribute('stroke-width', '1');
                        infraNode.style.filter = '';
                    }
                }
            }
        });
    }

    // =========================================================================
    // Utilities
    // =========================================================================

    private truncateLabel(label: string, maxChars: number): string {
        return label.length > maxChars
            ? label.substring(0, maxChars - 1) + '…'
            : label;
    }
}
